import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Smartphone, Download } from 'lucide-react';

// Conservative iOS detection — iPhone and iPad (including iPadOS desktop-mode UA).
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document);
}

const IOS_BROWSER = isIOS();

const APP_STORE_URL = 'https://apps.apple.com/app/filmple/id6759590295';

// Routes that must remain accessible on iOS web.
const EXEMPT_PATHS = [
  '/reset-password',
  '/privacy-policy',
  '/terms',
  '/cookie-policy',
  '/disclaimer',
];

function isRouteExempt(pathname: string): boolean {
  if (EXEMPT_PATHS.includes(pathname)) return true;

  // /auth/callback is unconditionally exempt — AuthCallback.tsx owns all
  // auth callback UX (signup interstitial, recovery, email_change).
  if (pathname === '/auth/callback') return true;

  return false;
}

// Routes supported by the native DeepLinkHandler.swift.
// Patterns use simple prefix matching — order does not matter.
const SUPPORTED_DEEP_LINK_PATTERNS = [
  /^\/movie\/\d+$/,
  /^\/tv\/\d+$/,
  /^\/person\/\d+$/,
  /^\/u\/[^/]+(\/followers|\/following)?$/,
  /^\/list\/[^/]+$/,
  /^\/lists\/deleted$/,
  /^\/inbox(\/[^/]+)?$/,
  /^\/notifications$/,
  /^\/for-you$/,
  /^\/filmply$/,
  /^\/feel$/,
  /^\/settings$/,
  /^\/pricing$/,
];

// Build a filmple:// deep link from the current web route.
// Only emits deep links for routes the native app actually handles;
// unsupported routes fall back to app root (discover).
function buildDeepLink(pathname: string, search: string): string {
  if (pathname === '/') return 'filmple://discover';

  const isSupported = SUPPORTED_DEEP_LINK_PATTERNS.some(p => p.test(pathname));
  if (isSupported) return `filmple:/${pathname}${search}`;

  return 'filmple://discover';
}

/**
 * Full-screen blocking gate for iOS web browsers.
 * Renders children normally on non-iOS browsers and for exempt routes.
 * On blocked iOS routes, shows a gate pushing users to the native app.
 */
export function IOSWebGate({ children }: { children: ReactNode }) {
  const location = useLocation();

  if (!IOS_BROWSER) return <>{children}</>;
  if (isRouteExempt(location.pathname)) return <>{children}</>;

  return (
    <IOSGateScreen
      pathname={location.pathname}
      search={location.search}
    />
  );
}

function IOSGateScreen({ pathname, search }: { pathname: string; search: string }) {
  const deepLink = buildDeepLink(pathname, search);
  const [attempted, setAttempted] = useState(false);

  // Auto-open attempt — fire once on mount.
  useEffect(() => {
    if (attempted) return;
    setAttempted(true);
    window.location.href = deepLink;
  }, [deepLink, attempted]);

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-4">
      <div className="text-center p-8 max-w-sm w-full">
        <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-6">
          <Smartphone size={32} className="text-cyan-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Open Filmple in the app</h1>
        <p className="text-sm text-slate-400 mb-8">
          Filmple on iPhone and iPad is designed for the native app.
          Download it or open it to get the full experience.
        </p>
        <div className="flex flex-col gap-3">
          <a
            href={deepLink}
            className="w-full py-3 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <Smartphone size={18} />
            Open in App
          </a>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-3 bg-[#1A1F26] hover:bg-[#252B33] text-slate-300 font-medium rounded-xl transition-colors ring-1 ring-white/[0.08] flex items-center justify-center gap-2"
          >
            <Download size={18} />
            Get the App
          </a>
        </div>
        <div className="mt-6 flex justify-center gap-4 text-xs text-slate-600">
          <a href="/privacy-policy" className="hover:text-slate-400 transition-colors">Privacy</a>
          <a href="/terms" className="hover:text-slate-400 transition-colors">Terms</a>
        </div>
      </div>
    </div>
  );
}
